import {
  Button,
  Checkbox,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from "@material-ui/core";
import AddCircleOutlineOutlinedIcon from "@material-ui/icons/AddCircleOutlineOutlined";
import CheckCircleOutlineOutlinedIcon from "@material-ui/icons/CheckCircleOutlineOutlined";
import ListAltOutlinedIcon from "@material-ui/icons/ListAltOutlined";
import RemoveCircleOutlineOutlinedIcon from "@material-ui/icons/RemoveCircleOutlineOutlined";
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from "react";
import { FaRegTrashAlt } from "react-icons/fa";
import { LENDER_REQUEST_CHANGE, LINE_ITEM_STATUS, REQUEST_STATUSES } from "../../../shared/constants/RequestStatuses";

import "./ReasonCodesReviewSection.css";

const reasonCodeComparer = (a, b) => (a.reasonCode || "").localeCompare(b.reasonCode || "");

/**
 * mapReasonCodeOutcome
 *
 * Pure 6-rule mapping (verbatim from FEATURE_SPEC.md §8):
 *   Removed + Accept -> Remove from Final
 *   Added   + Accept -> Add to Final
 *   Removed + Reject -> Add to Final (retained)
 *   Added   + Reject -> Remove from Final (never added)
 *   Removed + Modify -> Remove from Final
 *   Added   + Modify -> Add to Final
 *
 * Returns true (Add to Final), false (Remove from Final), or undefined for unrecognized inputs.
 * Direction (lenderRequestChange) is always an explicit input, never re-derived here.
 */
export const mapReasonCodeOutcome = (lenderRequestChange, status) => {
  if (lenderRequestChange === LENDER_REQUEST_CHANGE.REMOVED) {
    return status === LINE_ITEM_STATUS.REJECT;
  }
  if (lenderRequestChange === LENDER_REQUEST_CHANGE.ADDED) {
    return status !== LINE_ITEM_STATUS.REJECT;
  }
  return undefined;
};

/**
 * computeFinalReasonCodeIds
 *
 * Applies mapReasonCodeOutcome to each decision. Active reason codes with no matching
 * decision entry pass through to Final Reason Codes unchanged.
 */
export const computeFinalReasonCodeIds = (activeReasonCodeIds, decisions) => {
  const finalIds = new Set(activeReasonCodeIds || []);

  (decisions || []).forEach(({ reasonId, lenderRequestChange, status }) => {
    const include = mapReasonCodeOutcome(lenderRequestChange, status);
    if (include === true) finalIds.add(reasonId);
    else if (include === false) finalIds.delete(reasonId);
  });

  return Array.from(finalIds);
};

const renderSectionHeader = (title, IconComponent, toneClass) => (
  <h3 className="request-details-subheader review-reason-codes-header" style={{ marginBottom: 10 }}>
    <span className={`review-reason-codes-header-icon ${toneClass}`} aria-hidden="true">
      <IconComponent fontSize="small" />
    </span>
    <span>{title}</span>
  </h3>
);

const renderReasonCodeList = (reasonCodes, emptyMessage) => (
  !reasonCodes.length ? (
    <p className="no-reason-codes-message">{emptyMessage}</p>
  ) : (
    <Table className="review-reason-codes-table">
      <TableHead>
        <TableRow>
          <TableCell className="reason-code-name-column">Reason Code</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {reasonCodes.map((reasonCode) => (
          <TableRow key={reasonCode.reasonId}>
            <TableCell className="reason-code-name-column">{reasonCode.reasonCode || "-"}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
);

const renderReasonCodeBadgeList = (reasonCodes, emptyMessage) => (
  !reasonCodes.length ? (
    <p className="no-reason-codes-message">{emptyMessage}</p>
  ) : (
    <div className="review-reason-codes-simple-list">
      {reasonCodes.map((reasonCode) => (
        <span key={reasonCode.reasonId} className="reason-code-badge">
          {reasonCode.reasonCode || "-"}
        </span>
      ))}
    </div>
  )
);

/**
 * ReasonCodesReviewSection
 *
 * Renders Current / Requested-to-Remove / Requested-to-Add reason codes (always read-only),
 * plus a combined Final Reason Codes section: editable (Accept/Reject + reviewer add/remove)
 * while actively reviewing, static/read-only once decided (showDecisionStatus).
 */
const ReasonCodesReviewSection = ({
  activeReasonCodeIds,
  requestStatusChangeReasonCodes,
  reasonCodeOptions,
  showDecisionStatus,
  canReviewReasonCodes,
  onReasonCodeDecisionsChange,
  statusName
}) => {
  const reasonCodeMap = useMemo(
    () => new Map((reasonCodeOptions || []).map(rc => [rc.reasonId, rc])),
    [reasonCodeOptions]
  );

  const activeIdSet = useMemo(() => new Set(activeReasonCodeIds || []), [activeReasonCodeIds]);

  const currentReasonCodes = useMemo(() => (
    (activeReasonCodeIds || [])
      .map(id => reasonCodeMap.get(id))
      .filter(Boolean)
      .sort(reasonCodeComparer)
  ), [activeReasonCodeIds, reasonCodeMap]);

  const requestedRemoveReasonCodes = useMemo(() => (
    (requestStatusChangeReasonCodes || [])
      .filter(rc => rc.lenderRequestChange === LENDER_REQUEST_CHANGE.REMOVED && rc.status !== LINE_ITEM_STATUS.MODIFY)
      .map(rc => reasonCodeMap.get(rc.reasonId))
      .filter(Boolean)
      .sort(reasonCodeComparer)
  ), [requestStatusChangeReasonCodes, reasonCodeMap]);

  const requestedAddReasonCodes = useMemo(() => (
    (requestStatusChangeReasonCodes || [])
      .filter(rc => rc.lenderRequestChange === LENDER_REQUEST_CHANGE.ADDED && rc.status !== LINE_ITEM_STATUS.MODIFY)
      .map(rc => reasonCodeMap.get(rc.reasonId))
      .filter(Boolean)
      .sort(reasonCodeComparer)
  ), [requestStatusChangeReasonCodes, reasonCodeMap]);

  const [acceptedRequestedIds, setAcceptedRequestedIds] = useState(new Set());
  const [extraDecisions, setExtraDecisions] = useState([]);
  const [isAddingExtra, setIsAddingExtra] = useState(false);

  useEffect(() => {
    // Default every requested item to Accept until the reviewer rejects it.
    setAcceptedRequestedIds(new Set((requestStatusChangeReasonCodes || []).map(rc => rc.reasonId)));
  }, [requestStatusChangeReasonCodes]);

  const decidedReasonCodeIds = useMemo(
    () => computeFinalReasonCodeIds(activeReasonCodeIds, requestStatusChangeReasonCodes),
    [activeReasonCodeIds, requestStatusChangeReasonCodes]
  );

  const reviewingDecisions = useMemo(() => {
    const requestedDecisions = (requestStatusChangeReasonCodes || []).map(rc => ({
      reasonId: rc.reasonId,
      lenderRequestChange: rc.lenderRequestChange,
      status: acceptedRequestedIds.has(rc.reasonId) ? LINE_ITEM_STATUS.ACCEPT : LINE_ITEM_STATUS.REJECT,
    }));

    const extraDecisionsWithStatus = extraDecisions.map(d => ({
      reasonId: d.reasonId,
      lenderRequestChange: d.lenderRequestChange,
      status: LINE_ITEM_STATUS.MODIFY,
    }));

    return [...requestedDecisions, ...extraDecisionsWithStatus];
  }, [requestStatusChangeReasonCodes, acceptedRequestedIds, extraDecisions]);

  const reviewingReasonCodeIds = useMemo(
    () => computeFinalReasonCodeIds(activeReasonCodeIds, reviewingDecisions),
    [activeReasonCodeIds, reviewingDecisions]
  );

  const finalReasonCodeIds = showDecisionStatus ? decidedReasonCodeIds : reviewingReasonCodeIds;

  const finalReasonCodes = useMemo(() => (
    finalReasonCodeIds
      .map(id => reasonCodeMap.get(id))
      .filter(Boolean)
      .sort(reasonCodeComparer)
  ), [finalReasonCodeIds, reasonCodeMap]);

  // Keep the parent synced only for editable mode.
  useEffect(() => {
    if (!showDecisionStatus && onReasonCodeDecisionsChange) {
      onReasonCodeDecisionsChange(
        reviewingDecisions.map(d => ({
          reasonCodeId: d.reasonId,
          status: d.status,
          lenderRequestChange: d.lenderRequestChange,
        }))
      );
    }
  }, [showDecisionStatus, onReasonCodeDecisionsChange, reviewingDecisions]);

  const alreadyDecidedIdSet = useMemo(() => {
    const ids = new Set((requestStatusChangeReasonCodes || []).map(rc => rc.reasonId));
    extraDecisions.forEach(d => ids.add(d.reasonId));
    return ids;
  }, [requestStatusChangeReasonCodes, extraDecisions]);

  const remainingReasonCodeOptions = useMemo(() => (
    (reasonCodeOptions || [])
      .filter(rc => !alreadyDecidedIdSet.has(rc.reasonId))
      .sort(reasonCodeComparer)
  ), [reasonCodeOptions, alreadyDecidedIdSet]);

  const showAddExtraControl = canReviewReasonCodes && !showDecisionStatus && remainingReasonCodeOptions.length > 0;

  const handleAcceptToggle = (reasonId, isAccepted) => {
    setAcceptedRequestedIds((prevAcceptedIds) => {
      const newSet = new Set(prevAcceptedIds);
      if (isAccepted) newSet.add(reasonId);
      else newSet.delete(reasonId);
      return newSet;
    });
  };

  const handleAddExtraReasonCode = (reasonId) => {
    const reasonCode = reasonCodeMap.get(reasonId);
    if (!reasonCode) return;

    const lenderRequestChange = activeIdSet.has(reasonId) ? LENDER_REQUEST_CHANGE.REMOVED : LENDER_REQUEST_CHANGE.ADDED;
    setExtraDecisions((prev) => [...prev, { reasonId, lenderRequestChange }]);
    setIsAddingExtra(false);
  };

  const handleRemoveExtraReasonCode = (reasonId) => {
    setExtraDecisions((prev) => prev.filter((d) => d.reasonId !== reasonId));
  };

  return (
    <>
      <div className="review-reason-codes-section">
        {renderSectionHeader("Current Reason Codes", ListAltOutlinedIcon, "current-reason-codes-tone")}
        {renderReasonCodeBadgeList(currentReasonCodes, "No reason codes currently active on project.")}
      </div>

      <div className="review-reason-codes-section">
        {renderSectionHeader("Requested Reason Codes to Remove", RemoveCircleOutlineOutlinedIcon, "requested-remove-reason-codes-tone")}
        {renderReasonCodeBadgeList(requestedRemoveReasonCodes, "No reason codes requested for removal.")}
      </div>

      <div className="review-reason-codes-section">
        {renderSectionHeader("Requested Reason Codes to Add", AddCircleOutlineOutlinedIcon, "requested-add-reason-codes-tone")}
        {renderReasonCodeBadgeList(requestedAddReasonCodes, "No reason codes requested to add.")}
      </div>

      {/* Final Reason Codes section: shown in reviewing mode (showDecisionStatus=false), or in read-only mode (showDecisionStatus=true) only if status is one of the reviewed statuses */}
      {(!showDecisionStatus || (statusName === REQUEST_STATUSES.ACTION_TAKEN || statusName === REQUEST_STATUSES.NO_ACTION_TAKEN || statusName === REQUEST_STATUSES.PARTIAL_ACTION_TAKEN || statusName === 'Completed' || statusName === 'Declined' || statusName === 'Partially Completed')) && (
      <div className="review-reason-codes-section">
        {renderSectionHeader("Final Reason Codes", CheckCircleOutlineOutlinedIcon, "final-reason-codes-tone")}

        {!showDecisionStatus && (requestStatusChangeReasonCodes || []).length > 0 && (
          <Table className="review-reason-codes-table requested-decision-table" style={{ marginBottom: 0 }}>
            <TableHead>
              <TableRow>
                <TableCell className="reason-code-name-column">Reason Code</TableCell>
                <TableCell>Requested Change</TableCell>
                {canReviewReasonCodes && <TableCell className="checkbox-column">Accept</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {(requestStatusChangeReasonCodes || []).map((rc) => {
                const reasonCode = reasonCodeMap.get(rc.reasonId);
                const isAccepted = acceptedRequestedIds.has(rc.reasonId);

                return (
                  <TableRow key={rc.reasonId}>
                    <TableCell className="reason-code-name-column">{reasonCode?.reasonCode || "-"}</TableCell>
                    <TableCell>{rc.lenderRequestChange}</TableCell>
                    {canReviewReasonCodes && (
                      <TableCell className="checkbox-column">
                        <Checkbox
                          checked={isAccepted}
                          onChange={(e) => handleAcceptToggle(rc.reasonId, e.target.checked)}
                          style={{ color: "#085280" }}
                          inputProps={{ "aria-label": `Accept ${reasonCode?.reasonCode || rc.reasonId}` }}
                        />
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        {!showDecisionStatus && showAddExtraControl && (
          <>
            {(extraDecisions.length > 0 || isAddingExtra) && (
              <Table className="review-reason-codes-table added-reason-codes-table" style={{ marginTop: 0 }}>
                <TableBody>
                  {extraDecisions.map((d) => {
                    const reasonCode = reasonCodeMap.get(d.reasonId);
                    return (
                      <TableRow key={d.reasonId}>
                        <TableCell className="reason-code-name-column">{reasonCode?.reasonCode || "-"}</TableCell>
                        <TableCell>{d.lenderRequestChange}</TableCell>
                        <TableCell className="cancel-add-reason-code-column">
                          <IconButton
                            size="small"
                            aria-label="Remove extra reason code"
                            onClick={() => handleRemoveExtraReasonCode(d.reasonId)}
                          >
                            <FaRegTrashAlt style={{ fill: "red" }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {isAddingExtra && (
                    <TableRow className="insert-reason-code-row">
                      <TableCell style={{ width: 'auto' }} colSpan={2}>
                        <select
                          className="add-reason-code-select"
                          value=""
                          aria-label="Select reason code to add or remove"
                          onChange={(e) => handleAddExtraReasonCode(Number(e.target.value))}
                        >
                          <option value="" disabled>Select a reason code...</option>
                          {remainingReasonCodeOptions.map((reasonCode) => (
                            <option key={reasonCode.reasonId} value={reasonCode.reasonId}>{reasonCode.reasonCode || "-"}</option>
                          ))}
                        </select>
                      </TableCell>
                      <TableCell className="cancel-add-reason-code-column">
                        <IconButton
                          size="small"
                          aria-label="Cancel adding reason code"
                          onClick={() => setIsAddingExtra(false)}
                        >
                          <FaRegTrashAlt style={{ fill: "red" }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
            <Button
              className="add-reason-code-button"
              onClick={() => setIsAddingExtra(true)}
              disabled={remainingReasonCodeOptions.length === 0}
              style={{ display: isAddingExtra ? 'none' : undefined }}
            >
              + Add/Remove Reason Code
            </Button>
          </>
        )}

        <div className="final-reason-codes-list">
          {renderReasonCodeList(finalReasonCodes, "No reason codes in Final Reason Codes.")}
        </div>
      </div>
      )}
    </>
  );
};

ReasonCodesReviewSection.propTypes = {
  activeReasonCodeIds: PropTypes.arrayOf(PropTypes.number),
  requestStatusChangeReasonCodes: PropTypes.arrayOf(PropTypes.shape({
    reasonId: PropTypes.number,
    status: PropTypes.string,
    lenderRequestChange: PropTypes.string,
  })),
  reasonCodeOptions: PropTypes.arrayOf(PropTypes.shape({
    reasonId: PropTypes.number,
    reasonCode: PropTypes.string,
  })),
  showDecisionStatus: PropTypes.bool,
  canReviewReasonCodes: PropTypes.bool,
  onReasonCodeDecisionsChange: PropTypes.func,
  statusName: PropTypes.string,
};

export default ReasonCodesReviewSection;
